import React from "react";

import isEmpty from "lodash.isempty";
import { observer } from "mobx-react";
import Link from "next/link";
import PropTypes from "prop-types";
import { Nav } from "react-bootstrap";
import styled, { keyframes } from "styled-components";

import { useStores } from "../../contexts/StoreContext";
import breakpoints from "../../shared/constants";

const fadeIn = keyframes`
  from {opacity: 0}
  to { opacity: 1}
`;

const StyledRoleBrowser = styled.div`
  position: absolute;
  animation: ${fadeIn};
  animation-duration: 0.5s;
  animation-iteration-count: 1;
  left: 0;
  top: 0;
  background-image: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0) 50%,
    rgba(0, 0, 0, 0.45) 80%,
    rgba(0, 0, 0, 0.8) 100%
  );
`;

const StyledNavLink = styled(Nav.Link)`
  width: 180px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  background-color: ${({ theme }) => theme.primaryColor};
  color: ${({ theme }) => theme.negativeTextColor};
  border: none;
  outline: none;
  position: ${({ positionX, positionY }) =>
    !isEmpty(positionX) && !isEmpty(positionY) ? "fixed" : "static"} !important;
  text-transform: uppercase;

  @media (min-width: ${breakpoints.XL_DESKTOP}) {
    top: ${({ positionX }) => positionX?.lg} !important;
    left: ${({ positionY }) => positionY?.lg} !important;
  }

  @media (max-width: ${breakpoints.XL_DESKTOP}) {
    top: ${({ positionX }) => positionX?.md} !important;
    left: ${({ positionY }) => positionY?.md} !important;
  }

  @media (max-width: ${breakpoints.DESKTOP}) {
    top: ${({ positionX }) => positionX?.sm} !important;
    left: ${({ positionY }) => positionY?.sm} !important;
  }

  &:hover {
    background-color: ${({ theme }) => theme.primaryBackgroundColor};
    color: ${({ theme }) => theme.textColor};
  }
`;

function StyledRoleLink({
  as,
  children,
  className,
  href,
  positionX,
  positionY,
}) {
  return (
    <Link as={as} href={href} passHref legacyBehavior>
      <StyledNavLink
        className={className}
        positionX={positionX}
        positionY={positionY}
      >
        {children}
      </StyledNavLink>
    </Link>
  );
}

function InteractiveRoleBrowser() {
  const { rolesStore } = useStores();
  const { roles } = rolesStore;

  return (
    <StyledRoleBrowser className="h-100 w-100">
      <div className="container-fluid row">
        {roles.map((role) => (
          <StyledRoleLink
            key={role.slug}
            href={`roles/${role.slug}`}
            positionX={role?.additionalConfiguration?.buttons?.posx}
            positionY={role?.additionalConfiguration?.buttons?.posy}
          >
            {role.roleName}
          </StyledRoleLink>
        ))}
      </div>
    </StyledRoleBrowser>
  );
}
export default observer(InteractiveRoleBrowser);

StyledRoleLink.propTypes = {
  as: PropTypes.string,
  children: PropTypes.string,
  className: PropTypes.string,
  href: PropTypes.string,
  positionX: PropTypes.shape({
    lg: PropTypes.string,
    md: PropTypes.string,
    sm: PropTypes.string,
  }),
  positionY: PropTypes.shape({
    lg: PropTypes.string,
    md: PropTypes.string,
    sm: PropTypes.string,
  }),
};

StyledRoleLink.defaultProps = {
  as: null,
  children: null,
  className: null,
  href: null,
  positionX: {
    lg: "0%",
    md: "0%",
    sm: "0%",
  },
  positionY: {
    lg: "0%",
    md: "0%",
    sm: "0%",
  },
};
